import React, { useState, useEffect } from 'react';

const IpObjectForm = ({ ipObjectId = null, ipObjectData = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    status: 'active',
    metadata: {},
    owner_ids: [],
    available_rights_category_ids: []
  });
  const [categories, setCategories] = useState([]);
  const [parties, setParties] = useState([]);
  const [rightsCategories, setRightsCategories] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isEditMode = !!ipObjectId;

  useEffect(() => {
    fetchCategories();
    fetchParties();
    fetchRightsCategories();
    if (isEditMode && !ipObjectData) {
      fetchIpObject();
    } else if (ipObjectData) {
      setFormData({
        name: ipObjectData.name || '',
        description: ipObjectData.description || '',
        category_id: ipObjectData.category_id || '',
        status: ipObjectData.status || 'active',
        metadata: ipObjectData.metadata || {},
        owner_ids: ipObjectData.owners?.map(o => o.id) || [],
        available_rights_category_ids: ipObjectData.available_rights_categories?.map(rc => rc.id) || []
      });
    }
  }, [ipObjectId, ipObjectData]);

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/v1/categories');
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchParties = async () => {
    try {
      const response = await fetch('/api/v1/parties?per_page=100');
      const data = await response.json();
      setParties(data.parties || []);
    } catch (error) {
      console.error('Error fetching parties:', error);
    }
  };

  const fetchRightsCategories = async () => {
    try {
      const response = await fetch('/api/v1/rights_categories');
      const data = await response.json();
      setRightsCategories(data);
    } catch (error) {
      console.error('Error fetching rights categories:', error);
    }
  };

  const fetchIpObject = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/ip_objects/${ipObjectId}`);
      const data = await response.json();
      setFormData({
        name: data.name || '',
        description: data.description || '',
        category_id: data.category_id || '',
        status: data.status || 'active',
        metadata: data.metadata || {},
        owner_ids: data.owners?.map(o => o.id) || [],
        available_rights_category_ids: data.available_rights_categories?.map(rc => rc.id) || []
      });
    } catch (error) {
      console.error('Error fetching IP object:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMultiSelectChange = (e) => {
    const { name, options } = e.target;
    const selectedValues = Array.from(options)
      .filter(option => option.selected)
      .map(option => parseInt(option.value));

    setFormData(prev => ({
      ...prev,
      [name]: selectedValues
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors([]);

    try {
      const url = isEditMode
        ? `/api/v1/ip_objects/${ipObjectId}`
        : '/api/v1/ip_objects';

      const method = isEditMode ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ip_object: formData })
      });

      const data = await response.json();

      if (response.ok) {
        window.location.href = `/ip_objects/${data.id}`;
      } else {
        setErrors(data.errors || ['An error occurred']);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors(['Network error occurred']);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="ip-object-form-container">
      <div className="form-header">
        <h1>{isEditMode ? 'Edit IP Object' : 'New IP Object'}</h1>
        <a href="/ip_objects" className="btn btn-secondary">Cancel</a>
      </div>

      {errors.length > 0 && (
        <div className="alert alert-danger">
          <strong>Please fix the following errors:</strong>
          <ul>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="ip-object-form">
        <div className="form-group">
          <label htmlFor="name" className="required">Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            maxLength="255"
            className="form-control"
            placeholder="e.g., Mickey Mouse, Star Wars, Nike Swoosh"
          />
          <small className="form-text text-muted">
            Unique name for this IP asset
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="4"
            className="form-control"
            placeholder="Describe this IP asset..."
          />
        </div>

        <div className="form-group">
          <label htmlFor="category_id" className="required">Category</label>
          <select
            id="category_id"
            name="category_id"
            value={formData.category_id}
            onChange={handleChange}
            required
            className="form-control"
          >
            <option value="">Select a category...</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <small className="form-text text-muted">
            Type of IP asset (Character, Brand, Logo, etc.)
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="status" className="required">Status</label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
            className="form-control"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
          </select>
          <small className="form-text text-muted">
            Current status of this IP asset
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="owner_ids">Owners</label>
          <select
            id="owner_ids"
            name="owner_ids"
            value={formData.owner_ids}
            onChange={handleMultiSelectChange}
            multiple
            className="form-control"
            size="5"
          >
            {parties.map(party => (
              <option key={party.id} value={party.id}>
                {party.name} ({party.party_type.replace('_', ' ')})
              </option>
            ))}
          </select>
          <small className="form-text text-muted">
            Hold Ctrl (Cmd on Mac) to select multiple owners
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="available_rights_category_ids">Available Rights Categories</label>
          <select
            id="available_rights_category_ids"
            name="available_rights_category_ids"
            value={formData.available_rights_category_ids}
            onChange={handleMultiSelectChange}
            multiple
            className="form-control"
            size="5"
          >
            {rightsCategories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <small className="form-text text-muted">
            Hold Ctrl (Cmd on Mac) to select multiple rights categories
          </small>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary"
          >
            {submitting ? 'Saving...' : (isEditMode ? 'Update IP Object' : 'Create IP Object')}
          </button>
          <a href="/ip_objects" className="btn btn-secondary">Cancel</a>
        </div>
      </form>
    </div>
  );
};

export default IpObjectForm;
